// The "What you can make" showcase data (spec-v3-formats.md, Web E2).
//
// The live list comes from GET /api/formats (public, no auth), each format with an optional `sample` (a showcased
// render). The landing must never break, so it ships a static mirror of the core registry
// (product/core/src/formats/defs/index.ts): if the request fails, times out or returns something odd, the page shows
// this list instead. landing.test.ts checks the mirror against core, so a new format there fails the test until it
// has a card (and art) here. Pure: no DOM, no React.

export type FormatSample = { title: string; posterUrl: string; videoUrl: string; aspect: string; durationS: number };

/** What the landing needs of a format (a subset of core's FormatInfo). */
export type ShowFormat = {
  id: string;
  name: string;
  group: string;
  status: "ready" | "beta";
  tagline: string;
  /** something you could say to the Director */
  example: string;
  /** look names, in order */
  looks: string[];
  /** the format's default length in seconds (core `durations[0]`) */
  lengthS: number;
  sample: FormatSample | null;
};

/** The ad has real footage on the page already (the gallery loops), so its card uses that until a showcase exists. */
const AD_LOOP: FormatSample = { title: "Morning roast launch", posterUrl: "/landing/ad-roast.jpg", videoUrl: "/landing/ad-roast.mp4", aspect: "9:16", durationS: 15 };

/**
 * Mirror of core FORMAT_DEFS (names, groups, default length, first example, look names). The taglines are the
 * landing's own short ones, not core's: the page shows these even when the api answers (parseFormats).
 */
export const STATIC_FORMATS: ShowFormat[] = [
  { id: "ad", lengthS: 15, name: "Ad", group: "Marketing", status: "ready", tagline: "Product ads that stop the scroll.", example: "A 15 second TikTok ad for my coffee subscription", looks: ["Advertising photo", "Bold illustration"], sample: AD_LOOP },
  { id: "documentary", lengthS: 120, name: "Animated documentary", group: "Stories", status: "ready", tagline: "True stories, narrated and sourced.", example: "A three minute documentary on the first computer bug, from my notes", looks: ["Painted panels", "Archival", "Ink and wash", "Paper collage"], sample: null },
  { id: "cartoon", lengthS: 90, name: "Cartoon or short story", group: "Stories", status: "ready", tagline: "Your characters, voices and world.", example: "A bedtime story about a fox who's afraid of the dark", looks: ["Storybook", "2D cartoon", "Claymation", "Comic book", "3D animated"], sample: null },
  { id: "short-film", lengthS: 60, name: "Short film or trailer", group: "Stories", status: "ready", tagline: "Cinematic shots, a score and title cards.", example: "A trailer for a heist movie set on a space station", looks: ["Cinematic", "Noir", "Neon sci-fi", "Animated feature"], sample: null },
  { id: "math-explainer", lengthS: 90, name: "Math and science explainer", group: "Learning", status: "beta", tagline: "Equations and graphs that build on screen.", example: "Why the derivative of x squared is 2x", looks: ["Dark lesson", "Chalkboard", "Graph paper"], sample: null },
  { id: "explainer", lengthS: 90, name: "Character explainer", group: "Learning", status: "beta", tagline: "A host character walks through your idea.", example: "A friendly robot explains how solar panels make electricity", looks: ["2D cartoon", "Illustrated", "3D"], sample: null },
  { id: "short", lengthS: 45, name: "Faceless Short", group: "Social", status: "ready", tagline: "A hook, big captions and fast visuals.", example: "Three facts about octopuses that sound fake", looks: ["Bold captions", "Cinematic", "Illustrated"], sample: null },
  { id: "music-video", lengthS: 60, name: "Music video", group: "Music", status: "beta", tagline: "Visuals cut to your song's beat.", example: "A neon city story video for an 80s synth song", looks: ["Dreamy", "Neon", "Animated"], sample: null },
  { id: "slideshow", lengthS: 60, name: "Photo slideshow", group: "Personal", status: "ready", tagline: "Your photos, with motion and music.", example: "A two minute recap of our trip to Japan", looks: ["Warm", "Classic"], sample: null },
  { id: "kinetic", lengthS: 30, name: "Kinetic typography", group: "Social", status: "ready", tagline: "Bold words, timed to a voice.", example: "A motivational quote video about starting small", looks: ["Bold", "Minimal", "Neon"], sample: null },
  { id: "podcast", lengthS: 60, name: "Podcast clip", group: "Social", status: "beta", tagline: "Two hosts, live captions, a waveform.", example: "Two hosts debate whether remote work is here to stay", looks: ["Studio", "Minimal"], sample: null },
  { id: "demo", lengthS: 45, name: "Product demo", group: "Marketing", status: "beta", tagline: "Your app or site, walked through.", example: "A 45 second walkthrough of our dashboard", looks: ["Clean", "Dark"], sample: null },
];

/** The three headline types, shown large in the featured row (in this order). */
export const FEATURED = ["documentary", "cartoon", "short-film"] as const;

/** Group order below the featured row; a group the api adds later goes after these. */
export const GROUP_ORDER = ["Stories", "Social", "Learning", "Music", "Marketing", "Personal"];

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
/** Only same-origin paths or https URLs: the page never puts a javascript: or data: URL in a src. */
const url = (v: unknown): string | null => {
  const s = str(v);
  return s && (/^\/(?!\/)/.test(s) || /^https:\/\//i.test(s)) ? s : null;
};

export function parseSample(v: unknown): FormatSample | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  const videoUrl = url(o.videoUrl);
  const posterUrl = url(o.posterUrl);
  if (!videoUrl || !posterUrl) return null;
  const durationS = typeof o.durationS === "number" && Number.isFinite(o.durationS) && o.durationS > 0 ? o.durationS : 0;
  return { title: str(o.title) ?? "", posterUrl, videoUrl, aspect: str(o.aspect) ?? "16:9", durationS };
}

/**
 * GET /api/formats -> the showcase list. Formats the api knows are shown in its order; anything missing is filled
 * from the static mirror, and known formats keep the mirror's short tagline. Returns null when the payload isn't a usable list (the caller keeps the static one).
 */
export function parseFormats(json: unknown): ShowFormat[] | null {
  const list = Array.isArray(json) ? json : Array.isArray((json as { formats?: unknown })?.formats) ? (json as { formats: unknown[] }).formats : null;
  if (!list) return null;
  const byId = new Map(STATIC_FORMATS.map((f) => [f.id, f]));
  const out: ShowFormat[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    if (typeof raw !== "object" || raw === null) continue;
    const o = raw as Record<string, unknown>;
    const id = str(o.id);
    if (!id || seen.has(id)) continue;
    const base = byId.get(id);
    const name = str(o.name) ?? base?.name;
    const group = str(o.group) ?? base?.group;
    // the page's short tagline wins for a format it knows; the api's is used only for a format it hasn't seen
    const tagline = base?.tagline ?? str(o.tagline);
    if (!name || !group || !tagline) continue;
    const examples = Array.isArray(o.examples) ? o.examples.map(str).filter((x): x is string => !!x) : [];
    const durations = Array.isArray(o.durations) ? o.durations.filter((d): d is number => typeof d === "number" && d > 0) : [];
    const looks = Array.isArray(o.looks) ? o.looks.map((l) => str((l as { name?: unknown })?.name)).filter((x): x is string => !!x) : [];
    seen.add(id);
    out.push({
      id,
      name,
      group,
      status: o.status === "beta" ? "beta" : o.status === "ready" ? "ready" : (base?.status ?? "ready"),
      tagline,
      example: examples[0] ?? base?.example ?? "",
      looks: looks.length ? looks : (base?.looks ?? []),
      lengthS: durations[0] ?? base?.lengthS ?? 0,
      sample: parseSample(o.sample) ?? base?.sample ?? null,
    });
  }
  return out.length ? out : null;
}

export type Shelf = { group: string; formats: ShowFormat[] };

/** Featured formats (in FEATURED order) and the rest by group (GROUP_ORDER, then any new group by name). */
export function arrange(list: ShowFormat[]): { featured: ShowFormat[]; shelves: Shelf[] } {
  const featured = FEATURED.map((id) => list.find((f) => f.id === id)).filter((f): f is ShowFormat => !!f);
  const rest = list.filter((f) => !featured.includes(f));
  const groups = [...new Set(rest.map((f) => f.group))].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  return { featured, shelves: groups.map((group) => ({ group, formats: rest.filter((f) => f.group === group) })) };
}
const rank = (g: string) => {
  const i = GROUP_ORDER.indexOf(g);
  return i < 0 ? GROUP_ORDER.length : i;
};

/** Fetch the live list without auth; resolves to null on any failure (never throws, gives up after `timeoutMs`). */
export async function fetchFormats(fetcher: typeof fetch = fetch, timeoutMs = 5000): Promise<ShowFormat[] | null> {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const t = setTimeout(() => ctl?.abort(), timeoutMs);
  try {
    const r = await fetcher("/api/formats", { credentials: "omit", headers: { accept: "application/json" }, signal: ctl?.signal });
    if (!r.ok) return null;
    return parseFormats(await r.json());
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

/** Timecode: "00:45", "02:00" (minutes and seconds, as a monitor shows them). */
export const clock = (s: number) => {
  const t = Math.max(0, Math.round(s));
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
